<div *ngIf="session" class="card">
  <div class="card-header compact">
    <h1>
      <span test-id="session-label" [matTooltip]="session.label" matTooltipClass="tooltip-stretch" class="ellipsis">
        {{ session.label || ('sessionInfo.noLabel' | translate) }}
      </span>
      <small test-id="session-timestamp">{{ timestamp }}</small>
    </h1>
    <div class="card-actions">
      <app-button-group class="small fill-white">
        <button *ngIf="permissions.deleteContainer" test-id="delete-button" type="button" mat-stroked-button
          (click)="deleteSession()" [attr.aria-label]="'sessionInfo.deleteSession' | translate">
          <mat-icon class="xs">delete</mat-icon>
        </button>
        <button *ngIf="permissions.viewAuditTrail" test-id="audit-button" type="button" mat-stroked-button
          (click)="viewAuditTrail()" [attr.aria-label]="'queriesDialog.actions.viewAuditTrail' | translate"
          [matTooltip]="'queriesDialog.actions.viewAuditTrail' | translate">
          <mat-icon class="xs">list</mat-icon>
        </button>
        <button *ngIf="permissions.editMetadata" test-id="reset-button" type="button" mat-stroked-button (click)="resetForm()">
          {{ 'common.actions.reset' | translate }}
        </button>
      </app-button-group>
    </div>
  </div>

  <form [formGroup]="metadata">
    <div class="info-row">
      <span class="field-tooltip"></span>
      <span class="field-label">{{ 'common.labels.label' | translate }}</span>
      <div class="field-value">
        <span *ngIf="!permissions.editMetadata" test-id="label-text">{{ session.label }}</span>
        <input *ngIf="permissions.editMetadata" test-id="label-input" type="text" formControlName="label"
          [placeholder]="'sessionInfo.placeholders.label' | translate" autocomplete="off">
      </div>
      <div class="field-error">
        <span *ngIf="metadata.get('label').hasError('required')">{{ 'sessionInfo.errors.fieldRequired' | translate }}</span>
        <span *ngIf="metadata.get('label').hasError('minlength')">{{ 'common.errors.minLength' | translate }}</span>
        <span *ngIf="metadata.get('label').hasError('maxlength')">{{ 'common.errors.maxLength' | translate }}</span>
      </div>
      <div class="field-action-menu">
        <fw-action-menu test-id="label-actions" [auditable]="session"
          [fieldLabel]="'common.labels.label' | translate" fieldKey="label"></fw-action-menu>
      </div>
    </div>

    <div class="info-row">
      <span class="field-tooltip"></span>
      <span class="field-label">{{ 'common.labels.timestamp' | translate }}</span>
      <div class="field-value">
        <span *ngIf="!permissions.editMetadata" test-id="timestamp-text">{{ timestamp }}</span>
        <div *ngIf="permissions.editMetadata" class="datetime-field">
          <input test-id="timestamp-date-input" formControlName="timestampDate" matInput [matDatepicker]="datepicker"
            [placeholder]="'sessionInfo.placeholders.timestamp' | translate" autocomplete="off">
          <mat-datepicker-toggle test-id="timestamp-picker-toggle" [for]="datepicker" class="xs">
            <mat-icon matDatepickerToggleIcon class="xs">event</mat-icon>
          </mat-datepicker-toggle>
          <mat-datepicker #datepicker test-id="timestamp-picker"></mat-datepicker>
          <fw-time-field *ngIf="metadata.get('timestampDate').value"
            test-id="timestamp-time-field" formControlName="timestampTime"></fw-time-field>
          <app-typeahead test-id="timezone-field" formControlName="timezone" [options]="timezoneOptions"
            [placeholder]="'common.labels.timezone' | translate" class="timezone-typeahead full-width">
          </app-typeahead>
          <span *ngIf="sessionService.showTimezone$.value" test-id="timezone-local" style="margin: 0 5px;">{{ localSessionTimestamp }}</span>
        </div>
      </div>
      <div class="field-error">
        <span *ngIf="metadata.get('timestampDate').invalid">{{ 'sessionInfo.errors.dateInvalid' | translate }}</span>
        <span *ngIf="metadata.get('timestampDate').valid && metadata.get('timestampTime').invalid">{{ 'sessionInfo.errors.timeInvalid' | translate }}</span>
      </div>
      <div class="field-action-menu">
        <fw-action-menu test-id="timestamp-actions" [auditable]="session"
          [fieldLabel]="'common.labels.timestamp' | translate" fieldKey="timestamp"></fw-action-menu>
      </div>
    </div>

    <div class="info-row">
      <mat-icon class="field-tooltip xs" [matTooltip]="'sessionInfo.timeOfSessionTooltip' | translate">info</mat-icon>
      <span class="field-label" [innerHTML]="'sessionInfo.ageYearsLabel' | translate | trusthtml"></span>
      <div class="field-value">
        <span *ngIf="!permissions.editMetadata" test-id="age-text">{{ ageInYears }}</span>
        <input *ngIf="permissions.editMetadata" test-id="age-input" type="number" formControlName="age" min="0"
          [placeholder]="'sessionInfo.placeholders.age' | translate" autocomplete="off">
      </div>
      <div class="field-error">
        <span *ngIf="metadata.get('age').invalid">{{ 'sessionInfo.errors.agePositive' | translate }}</span>
      </div>
      <div class="field-action-menu">
        <fw-action-menu test-id="age-actions" [auditable]="session"
          [fieldLabel]="'sessionInfo.ageYears' | translate" fieldKey="age"></fw-action-menu>
      </div>
    </div>

    <div class="info-row">
      <mat-icon class="field-tooltip xs" [matTooltip]="'sessionInfo.timeOfSessionTooltip' | translate">info</mat-icon>
      <span class="field-label" [innerHTML]="'sessionInfo.weightKgLabel' | translate | trusthtml"></span>
      <div class="field-value">
        <span *ngIf="!permissions.editMetadata" test-id="weight-text">{{ session.weight }}</span>
        <input *ngIf="permissions.editMetadata" test-id="weight-input" type="number" formControlName="weight" min="0"
          [placeholder]="'sessionInfo.placeholders.weight' | translate" autocomplete="off">
      </div>
      <div class="field-error">
        <span *ngIf="metadata.get('weight').invalid">{{ 'sessionInfo.errors.weightPositive' | translate }}</span>
      </div>
      <div class="field-action-menu">
        <fw-action-menu test-id="weight-actions" [auditable]="session"
          [fieldLabel]="'sessionInfo.weightKg' | translate" fieldKey="weight"></fw-action-menu>
      </div>
    </div>

    <div class="info-row">
      <span class="field-tooltip"></span>
      <span class="field-label">{{ 'common.labels.operator' | translate }}</span>
      <div class="field-value">
        <span *ngIf="!permissions.editMetadata" test-id="operator-text">{{ session.operator }}</span>
        <input *ngIf="permissions.editMetadata" test-id="operator-input" type="text" formControlName="operator"
          [placeholder]="'sessionInfo.placeholders.operator' | translate" autocomplete="off">
      </div>
      <div class="field-error"></div>
      <div class="field-action-menu">
        <fw-action-menu test-id="operator-actions" [auditable]="session"
          [fieldLabel]="'common.labels.operator' | translate" fieldKey="operator"></fw-action-menu>
      </div>
    </div>
  </form>
</div>
